import React from 'react';
import PropTypes from 'prop-types';
import { getAmplitudeInstance } from '@alltrails/shared/utils/amplitudeHelpers';

import HeadText from 'components/shared/text/HeadText';
import BulletList from 'components/shared/text/BulletList';
import Expandable from 'components/shared/Expandable';
import PlanType from 'components/forms/pro/PlanType';
import PaymentForm from 'components/forms/pro/PaymentForm';
import LoginForm from 'components/forms/LoginForm';
import SignUpForm from 'components/forms/SignUpForm';
import CheckCircleIcon from 'images/pro/greenCheck-filled.svg';
import { PRO_PURCHASE_PAGE_VIEWED, PRO_PURCHASE_GET_STARTED_CLICKED } from 'utils/constants/AmplitudeEvent';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { ProPageStrings, StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { initializeOptimizely } from 'utils/optimizely_helpers';
import * as baseStyles from '../../styles/baseStyles.module.scss';
import * as styles from './styles/styles.module.scss';
import WebProPurchaseLocation from '../../amplitude/WebProPurchaseLocation';

const { GET_STARTED, GET_ALLTRAILS_PRO } = StringConstantsMixin.strings;

const PRO_PAGE_TEXT = { ...ProPageStrings.paymentText, creditCardForm: ProPageStrings.creditCardForm };

// These match the planCode values in ProService.plan_types
const PLAN_ANNUAL = 'annual';
const PLAN_THREE_YEAR = 'threeYr';
const PLAN_MONTHLY = 'monthly';

const RECURLY_PLAN_CODE_ANNUAL = 'alltrails_pro';
const RECURLY_PLAN_CODE_THREE_YEAR = 'alltrails_pro_3yr';
const RECURLY_PLAN_CODE_MONTHLY_599 = 'alltrails_pro_monthly_599';

const OPTIMIZELY_PRICING_FEATURE = 'pro_monthly_pricing';
const OPTIMIZELY_PRICE_VARIABLE = 'pro_monthly_price_dollars';
const OPTIMIZELY_RECURLY_VARIABLE = 'recurly_plan_code';

const UNITED_STATES_COUNTRY_ID = 313;

const OPTIMIZELY_CONTROL = 'control';

function getFeaturesContent() {
  const featuresList = <BulletList bullet={<img src={CheckCircleIcon} style={{ color: '#428a13' }} />} list={ProPageStrings.featuresList} />;

  return (
    <div className={`${styles.panel} ${styles.left}`}>
      <HeadText type="head3" text={ProPageStrings.headText} />

      <div className={styles.mobile}>
        <Expandable title={ProPageStrings.featuresCollapseHead}>{featuresList}</Expandable>
      </div>

      <div className={styles.desktop}>{featuresList}</div>
    </div>
  );
}

class ProPage extends React.Component {
  constructor(props) {
    super(props);

    const activePlanType = this.props.initialPlan ? this.props.initialPlan : PLAN_ANNUAL;

    const recurlyPlanCode = {
      [PLAN_ANNUAL]: RECURLY_PLAN_CODE_ANNUAL,
      [PLAN_THREE_YEAR]: RECURLY_PLAN_CODE_THREE_YEAR
    }[activePlanType];

    this.state = {
      submitting: false,
      serverError: null,
      shownErrors: {},
      isAuthed: this.props.isAuthed,
      isFeaturesCollapsed: true,
      isRegistrationShown: true,
      isTypeSelected: this.props.initialPlan != null,
      activePlanType,
      recurlyPlanCode,
      activePaymentTab: 0,
      name: null,
      number: null,
      cvv: null,
      'cc-exp-month': null,
      'cc-exp-year': null,
      postalCode: null,
      verifiedCoupon: this.props.coupon,
      couponCode: this.props.coupon ? this.props.coupon.code : null,
      isCouponApplied: false,
      planTypes: [...this.props.planTypes],
      optimizelyInstance: null,
      optimizelyUserId: null
    };
  }

  componentDidMount() {
    getAmplitudeInstance().logEvent(PRO_PURCHASE_PAGE_VIEWED);

    // Set this to true to test optimizely locally with incorrect country id
    const forcePricingExperiment = false;
    const isUSA = this.props.countryId === UNITED_STATES_COUNTRY_ID;

    this.setUpOptimizely();

    if (this.props.forceMonthlyPricePoint) {
      this.forceMonthlyLowPricePoint();
    } else if (forcePricingExperiment || isUSA) {
      this.setUpPricingExperiment();
    }

    if (this.state.verifiedCoupon) {
      this.setState({ isCouponApplied: true, isCouponInputExpanded: true, isCouponInputShown: true, submitting: false });
    }
  }

  forceMonthlyLowPricePoint() {
    const planTypes = [...this.state.planTypes];
    const monthlyPrice = 5.99;
    planTypes.forEach(planType => {
      const percentage = Math.floor((1 - planType.priceMonth / monthlyPrice) * 100);
      const amount = `Save ${percentage}%`;
      planType.discountOverride = true;
      planType.discountOverrideAmount = amount;
    });

    const experimentalPlan = {
      allowCoupon: false,
      numberOfMonths: '1',
      planCode: 'monthly',
      priceMonth: monthlyPrice,
      priceTotal: monthlyPrice,
      showDiscount: false,
      recurlyPlanCode: RECURLY_PLAN_CODE_MONTHLY_599
    };

    // We want the experimental monthly plan at the top, so unshift instead of push
    planTypes.unshift(experimentalPlan);

    this.setState({ planTypes });
  }

  setUpOptimizely() {
    const { locale, optimizelySettings } = this.props;

    try {
      const [optimizelyInstance, optimizelyUserId] = initializeOptimizely(optimizelySettings);
      const optimizelyAttributes = { locale };
      optimizelyInstance.setUser({ id: optimizelyUserId, attributes: optimizelyAttributes });
      this.setState({ optimizelyInstance, optimizelyUserId }, this.setUpPricingExperiment);
    } catch (error) {
      console.error('Error setting up Optimizely', error);
    }
  }

  setUpPricingExperiment() {
    const { optimizelyInstance, optimizelyUserId } = this.state;

    if (!(optimizelyInstance && optimizelyUserId)) {
      return;
    }

    const experimentEnabled = optimizelyInstance.isFeatureEnabled(OPTIMIZELY_PRICING_FEATURE, optimizelyUserId);

    if (experimentEnabled) {
      const monthlyPrice = optimizelyInstance.getFeatureVariableDouble(OPTIMIZELY_PRICING_FEATURE, OPTIMIZELY_PRICE_VARIABLE, optimizelyUserId);
      const recurlyPlanCode = optimizelyInstance.getFeatureVariableString(OPTIMIZELY_PRICING_FEATURE, OPTIMIZELY_RECURLY_VARIABLE, optimizelyUserId);

      // User is in control group. Do not offer monthly plan.
      if (recurlyPlanCode === OPTIMIZELY_CONTROL || monthlyPrice === 0) {
        return;
      }

      const planTypes = [...this.state.planTypes];

      planTypes.forEach(planType => {
        const percentage = Math.floor((1 - planType.priceMonth / monthlyPrice) * 100);
        const amount = `Save ${percentage}%`;
        planType.discountOverride = true;
        planType.discountOverrideAmount = amount;
      });

      const experimentalPlan = {
        allowCoupon: false,
        numberOfMonths: '1',
        planCode: 'monthly',
        priceMonth: monthlyPrice,
        priceTotal: monthlyPrice,
        showDiscount: false,
        recurlyPlanCode
      };

      // We want the experimental monthly plan at the top, so unshift instead of push
      planTypes.unshift(experimentalPlan);

      this.setState({ planTypes });
    }
  }

  clearErrors() {
    // Single function to clear shownErrors, server errors and coupon errors
    this.setState({
      shownErrors: {},
      couponError: '',
      serverError: null
    });
  }

  handleTypeClick = (activePlanType, recurlyPlanCode) => {
    this.setState({ activePlanType, recurlyPlanCode });
  };

  userFormSuccessCallback = data => {
    this.clearErrors();
    this.setState({ user: data.user, isAuthed: true, submitting: false });
  };

  handleRegistrationSuccess = data => {
    this.setState({ user: data.user, isAuthed: true });
  };

  handleRegisterClick = () => {
    this.clearErrors();
    this.setState({ isRegistrationShown: true });
  };

  handleLoginClick = () => {
    this.clearErrors();
    this.setState({ isRegistrationShown: false });
  };

  handleCouponCodeChange = event => {
    const { value } = event.target;
    this.setState({ couponCode: value, couponError: '' });
  };

  handleApplyCoupon = () => {
    this.handleCouponVerification(this.state.couponCode);
  };

  handleRemoveCoupon = () => {
    this.setState({
      isCouponInputExpanded: false,
      couponCode: null,
      isCouponApplied: false,
      verifiedCoupon: null
    });
  };

  clearSelectedType = () => {
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'change_plan']);
    this.setState({ isTypeSelected: false });
  };

  handleCouponVerification = couponCode => {
    // coupon_code is only allowed param in the API
    if (this.state.submitting) {
      // Not ideal, but this will prevent double submission (until loading state is added)
      return;
    }
    this.setState({ submitting: true });
    const data = {
      coupon_code: couponCode
    };
    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/coupons/verify',
      data,
      data => {
        this.setState({ verifiedCoupon: data[0], isCouponApplied: true, submitting: false });
        ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'coupon_code', data[0].code]);
      },
      err => {
        this.setState({ isCouponApplied: false, couponError: ProPageStrings.creditCardForm.couponCode.validation.generic, submitting: false });
      },
      () => {}
    );
  };

  handleShowCouponCodeInput = () => {
    this.setState({ isCouponInputExpanded: true });
  };

  getPlanTypesForm() {
    const { activePlanType } = this.state;

    const handleTypeSubmit = () => {
      this.clearErrors();

      const analyticsMessage = {
        [PLAN_MONTHLY]: 'plan_select_monthly',
        [PLAN_ANNUAL]: 'plan_select_annual',
        [PLAN_THREE_YEAR]: 'plan_select_three_year'
      }[activePlanType];

      ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_1', analyticsMessage]);
      ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_1', 'submit']);

      getAmplitudeInstance().logEvent(PRO_PURCHASE_GET_STARTED_CLICKED);

      this.setState({ isTypeSelected: true });
    };

    return (
      <PlanType
        buttonText={GET_STARTED}
        text={ProPageStrings.planTypeText}
        handleSubmit={handleTypeSubmit}
        handleClick={this.handleTypeClick}
        activePlanType={this.state.activePlanType}
        planTypes={this.state.planTypes}
        currencyCode={this.props.currencyCode}
        verifiedCoupon={this.state.verifiedCoupon}
        isCouponApplied={this.state.isCouponApplied}
        serverError={this.state.serverError}
      />
    );
  }

  getLoginForm() {
    return (
      <LoginForm
        locale={this.props.locale}
        handleLoginSuccess={this.userFormSuccessCallback}
        handleRegisterClick={this.handleRegisterClick}
        enableRecaptcha={this.props.enableRecaptcha}
        facebookAppId={this.props.facebookAppId}
      />
    );
  }

  getRegistrationForm() {
    return (
      <SignUpForm
        isProPage
        countryId={this.props.countryId}
        handleRegistrationSuccess={this.handleRegistrationSuccess}
        handleLoginClick={this.handleLoginClick} // questionable
        enableRecaptcha={this.props.enableRecaptcha}
        facebookAppId={this.props.facebookAppId}
        redirectTo={null}
      />
    );
  }

  getPaymentForm() {
    return (
      <PaymentForm
        submitText={GET_ALLTRAILS_PRO}
        text={PRO_PAGE_TEXT}
        locale={this.props.locale}
        recurlyPublicKey={this.props.recurlyPublicKey}
        activePlanType={this.state.activePlanType}
        recurlyPlanCode={this.state.recurlyPlanCode}
        forceMonthlyPricePoint={this.props.forceMonthlyPricePoint}
        planTypes={this.state.planTypes}
        handleApplycoupon={this.handleApplyCoupon}
        handleCouponCodeChange={this.handleCouponCodeChange}
        handleShowCouponCodeInput={this.handleShowCouponCodeInput}
        handleRemoveCoupon={this.handleRemoveCoupon}
        isCouponApplied={this.state.isCouponApplied}
        verifiedCoupon={this.state.verifiedCoupon}
        couponError={this.state.couponError} // Coupon Codes use a different error messaging system since they verify when applying
        isCouponInputExpanded={this.state.isCouponInputExpanded}
        isCouponInputShown={this.state.activePlanType === PLAN_ANNUAL}
        updateActivePlan={this.handleTypeClick}
        activePaymentTab={this.state.activePaymentTab}
        paymentTypes={this.props.paymentTypes}
        enableRecaptcha={this.props.enableRecaptcha}
        amazonClientId={this.props.amazonClientId}
        amazonSellerId={this.props.amazonSellerId}
        couponCode={this.state.couponCode}
        currencyCode={this.props.currencyCode}
        optimizelyInstance={this.state.optimizelyInstance}
        optimizelyUserId={this.state.optimizelyUserId}
        optimizelyAttributeData={this.props.optimizelyAttributeData}
        webProPurchaseLocation={WebProPurchaseLocation.ProPage}
      />
    );
  }

  render() {
    let formBody;

    if (this.state.isTypeSelected) {
      if (this.state.isAuthed) {
        formBody = this.getPaymentForm();
      } else if (this.state.isRegistrationShown) {
        formBody = this.getRegistrationForm();
      } else {
        formBody = this.getLoginForm();
      }
    } else {
      formBody = this.getPlanTypesForm();
    }

    return (
      <div>
        <div className={baseStyles.container}>
          <div className={baseStyles.row}>
            <div className={`${styles.body} ${styles.topoBackground}`}>
              {!this.state.isTypeSelected && getFeaturesContent()}
              <div className={`${styles.panel} ${!this.state.isTypeSelected ? styles.right : ''}`}>{formBody}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ProPage.propTypes = {
  isAuthed: PropTypes.bool,
  facebookAppId: PropTypes.number.isRequired,
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      details: PropTypes.string,
      showDiscount: PropTypes.bool,
      discountAmount: PropTypes.string
    })
  ),
  paymentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      tab: PropTypes.number,
      showIcon: PropTypes.bool,
      iconPath: PropTypes.string
    })
  ),
  optimizelySettings: PropTypes.object.isRequired,
  optimizelyAttributeData: PropTypes.shape({
    country: PropTypes.string,
    authenticated: PropTypes.bool,
    mobileBrowser: PropTypes.bool
  }).isRequired
};

export default ProPage;
export { PLAN_MONTHLY, PLAN_ANNUAL, PLAN_THREE_YEAR };
